import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  // Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { post } from 'api';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';
import IconifyIcon from 'components/base/IconifyIcon';

const validationSchema = yup.object().shape({
  status: yup
    .string()
    .oneOf(['Dispatched', 'Hold', 'InProgress'])
    .required('Dispatch status is required'),
  type: yup.string().oneOf(['Full', 'Partial']).required('Dispatch type is required'),
  remark: yup.string().optional(),
});

const AddDispatch = ({ open, setOpen, customer, update, setUpdate, fetch }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      status: '',
      type: '',
      remark: '',
    },
  });

  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [dispatchItems, setDispatchItems] = useState([]);
  const [originalDispatchItems, setOriginalDispatchItems] = useState([]);
  const [data, setData] = useState([]);

  const fetchProduct = async () => {
    try {
      const res = await post('users/dropdown_product', {});
      if (res.status == 200) {
        setData(res.data);
      } else {
        enqueueSnackbar(res.message || 'Error while Fetching Product', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error while Fetching Product', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  useEffect(() => {
    fetchProduct();
  }, []);
  useEffect(() => {
    if (update) {
      // Prefill form fields
      reset({
        status: update.status || '',
        type: update.type || '',
        remark: update.remark || '',
      });

      // Prefill dispatch products
      const formattedItems = update.dispatch.map((item) => {
        const product = data.find((d) => d._id == item.productId._id);
        return {
          productId: item.productId._id,
          productName: item.productId.productName,
          qnt: item.qnt,
          currentStock: product.currentQnt,
          unitType: product.unitType,
          uniqueId: item._id,
        };
      });

      setDispatchItems(formattedItems);
      setOriginalDispatchItems(formattedItems);
    }
  }, [update, reset]);

  const handleAddItem = () => {
    if (!selectedItem || !quantity) return;
    const qty = Number(quantity);
    if (qty <= 0 || qty > selectedItem.currentQnt) return;

    if (dispatchItems.find((i) => i.itemId === selectedItem._id)) {
      alert('Item already added!');
      return;
    }

    setDispatchItems((prev) => [
      ...prev,
      {
        productId: selectedItem._id,
        productName: selectedItem.productName,
        currentStock: selectedItem.currentQnt,
        unitType: selectedItem.unitType,
        qnt: qty,
      },
    ]);
    setSelectedItem(null);
    setQuantity('');
  };

  const handleDeleteItem = (id) => {
    setDispatchItems((prev) => prev.filter((i) => i.productId !== id));
  };

  const areDispatchEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false;

    return a.every((item, idx) => {
      const other = b[idx];
      // compare the fields that matter for backend
      return item.productId === other.productId && Number(item.qnt) === Number(other.qnt);
    });
  };

  const onSubmit = async (data) => {
    if (dispatchItems.length === 0) {
      alert('Please add at least one item to dispatch!');
      return;
    }
    let res;
    const payload = {
      ...data,

      customerId: customer._id,
    };
    if (update) {
      payload._id = update._id;
      const changed = !areDispatchEqual(originalDispatchItems, dispatchItems);

      if (changed) {
        payload.dispatch = dispatchItems;
      }

      res = await post('users/update_dispatch', payload);
    } else {
      payload.dispatch = dispatchItems;
      res = await post('users/create_dispatch', payload);
    }

    if (res.status == 200) {
      enqueueSnackbar(res.message || `Dispatch ${update ? 'Changed' : 'Created'} Successfully`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } else {
      enqueueSnackbar(res.message || `Error While Dispatch ${update ? 'Changed' : 'Created'} `, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
    fetchProduct();
    reset();
    setDispatchItems([]);
    setOpen(false);
    fetch();
    setUpdate(null);
  };

  const handleClose = () => {
    reset();
    setDispatchItems([]);
    fetch();
    setOpen(false);
    setUpdate(null);
  };

  const isAddDisabled =
    !selectedItem ||
    !quantity ||
    Number(quantity) <= 0 ||
    Number(quantity) > selectedItem?.currentQnt;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 3, pb: 2 }}
      >
        <DialogTitle sx={{ p: 0 }}>{update ? 'Edit Dispatch' : 'Add Dispatch'}</DialogTitle>

        <IconButton onClick={handleClose}>
          <IconifyIcon icon="material-symbols:close-rounded" />
        </IconButton>
      </Stack>

      {/* Body */}
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Grid container spacing={2}>
          {/* Dispatch Meta */}
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Material Status"
                  fullWidth
                  error={!!errors.status}
                  helperText={errors.status?.message}
                >
                  <MenuItem value="Dispatched">Dispatched</MenuItem>
                  <MenuItem value="Hold">Hold</MenuItem>
                  <MenuItem value="InProgress">In Progress</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Dispatch Type"
                  fullWidth
                  error={!!errors.type}
                  helperText={errors.type?.message}
                >
                  <MenuItem value="Full">Full</MenuItem>
                  <MenuItem value="Partial">Partial</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          {/* Add Line Row */}
          <Grid item size={{ xs: 12 }}>
            <Typography variant="subtitle1" mb={1}>
              Add Dispatched Item
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                flexWrap: 'wrap',
                p: 2,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                background: '#fafafa',
              }}
            >
              {/* Select Item */}
              <TextField
                select
                label="Select Item"
                value={selectedItem?._id || ''}
                onChange={(e) => {
                  const item = data.find((i) => i._id === e.target.value);
                  setSelectedItem(item);
                  setQuantity('');
                }}
                sx={{ width: 300 }}
              >
                <MenuItem value="">Select...</MenuItem>
                {data.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.productName}
                  </MenuItem>
                ))}
              </TextField>

              {/* Quantity Field */}
              <TextField
                type="number"
                label="Quantity"
                value={quantity}
                disabled={!selectedItem}
                onChange={(e) => setQuantity(e.target.value)}
                sx={{ width: 140 }}
              />

              {/* Unit & Stock Info */}
              {selectedItem && (
                <Typography variant="body2" color="text.secondary">
                  Available:{' '}
                  <strong>
                    {selectedItem.currentQnt} {selectedItem.unitType}
                  </strong>
                </Typography>
              )}

              {/* Add Button */}
              <IconButton
                color="primary"
                sx={{
                  bgcolor: isAddDisabled ? 'action.disabledBackground' : 'primary.main',
                  color: isAddDisabled ? 'text.disabled' : '#fff',
                  '&:hover': {
                    bgcolor: !isAddDisabled ? 'primary.dark' : 'action.disabledBackground',
                  },
                }}
                onClick={handleAddItem}
                disabled={isAddDisabled}
              >
                <IconifyIcon icon="mdi:plus" />
              </IconButton>
            </Stack>
          </Grid>

          {/* Dispatch Table */}
          {dispatchItems.length > 0 && (
            <Grid item size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Table>
                  <TableHead sx={{ background: '#f4f5f7' }}>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Dispatch Qty</TableCell>
                      <TableCell>Available Stock</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dispatchItems.map((item, idx) => (
                      <TableRow key={item.itemId}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>
                          {item.qnt} {item.unitType}
                        </TableCell>
                        <TableCell>
                          {item.currentStock} {item.unitType}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteItem(item.productId)}
                          >
                            <IconifyIcon icon="mdi:delete-outline" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}

          {/* remark */}
          <Grid item size={{ xs: 12 }} sx={{ mt: 2 }}>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="remark" fullWidth multiline rows={3} />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={dispatchItems.length === 0}>
          Save Dispatch
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDispatch;
